// ─────────────────────────────────────────────────────────────────────────────
// Integração com o app nativo (Capacitor) — Android e, no futuro, iOS.
//
// TUDO que é específico de app mora aqui, num arquivo só. No navegador cada
// função destas vira no-op silencioso, então o site continua idêntico e nada
// aqui pode quebrar a web.
//
// Regra ao mexer: nunca deixe uma falha de plugin derrubar o app. Toda chamada
// nativa vai dentro de try/catch — plugin ausente, versão velha do Android ou
// permissão negada não podem virar tela branca no meio de um pedido.
// ─────────────────────────────────────────────────────────────────────────────
import { Capacitor } from '@capacitor/core';
import { App as AppNativo } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { Browser } from '@capacitor/browser';

/** true só dentro do APK/IPA. No navegador (inclusive PWA instalado) é false. */
export const ehNativo = () => {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

/** 'android' | 'ios' | 'web' */
export const plataforma = () => {
  try {
    return Capacitor.getPlatform();
  } catch {
    return 'web';
  }
};

// ─── Aviso de "toque de novo pra sair" ───────────────────────────────────────
// Proposital: NÃO usa o toast do app. Este aviso precisa funcionar mesmo se o
// React tiver caído — é a última coisa que o usuário vê antes do app fechar.
let _avisoEl = null;
const mostrarAviso = (texto) => {
  try {
    if (!_avisoEl) {
      _avisoEl = document.createElement('div');
      _avisoEl.setAttribute('role', 'status');
      _avisoEl.style.cssText = [
        'position:fixed',
        'left:50%',
        'transform:translateX(-50%)',
        'bottom:calc(28px + env(safe-area-inset-bottom))',
        'z-index:2147483647',
        'background:rgba(15,23,42,.94)',
        'color:#fff',
        'padding:11px 18px',
        'border-radius:999px',
        'font:600 13px/1 system-ui,-apple-system,sans-serif',
        'pointer-events:none',
        'opacity:0',
        'transition:opacity .18s ease',
      ].join(';');
      document.body.appendChild(_avisoEl);
    }
    _avisoEl.textContent = texto;
    requestAnimationFrame(() => {
      if (_avisoEl) _avisoEl.style.opacity = '1';
    });
    clearTimeout(_avisoEl._t);
    _avisoEl._t = setTimeout(() => {
      if (_avisoEl) _avisoEl.style.opacity = '0';
    }, 1900);
  } catch {}
};

// ─── Botão VOLTAR do Android ─────────────────────────────────────────────────
// Sem tratar isso, o botão físico FECHA o app de qualquer tela — é a reclamação
// nº 1 em avaliação de app que veio de web, e some direto pra 1 estrela.
//
// Ordem de decisão:
//   1. Alguém na tela quis interceptar (modal aberto, por exemplo) → respeita.
//   2. Existe histórico → volta uma tela (o popstate do App.js já cuida do resto).
//   3. Está na raiz → NÃO fecha de imediato: pede confirmação com toque duplo.
//      Fechar na primeira batida perde carrinho e formulário preenchido.
const JANELA_SAIDA_MS = 2200;
let _ultimoVoltar = 0;

const tratarVoltar = ({ canGoBack } = {}) => {
  // Ponto de extensão: qualquer tela pode chamar preventDefault() neste evento
  // pra fechar seu próprio modal em vez de deixar o app navegar.
  //   window.addEventListener('toalugando:voltar', (e) => { if (modalAberto) e.preventDefault(); })
  let seguir = true;
  try {
    seguir = window.dispatchEvent(new CustomEvent('toalugando:voltar', { cancelable: true }));
  } catch {}
  if (!seguir) return;

  const temHistorico = canGoBack === true || window.history.length > 1;
  if (temHistorico) {
    _ultimoVoltar = 0;
    try {
      window.history.back();
    } catch {}
    return;
  }

  const agora = Date.now();
  if (agora - _ultimoVoltar < JANELA_SAIDA_MS) {
    try {
      AppNativo.exitApp();
    } catch {}
    return;
  }
  _ultimoVoltar = agora;
  mostrarAviso('Toque de novo para sair');
};

// ─── Barra de status ─────────────────────────────────────────────────────────
// Só o STYLE é configurado aqui, e isso é deliberado.
//
// A partir do Android 15 o sistema força edge-to-edge, e a partir do targetSdk
// 36 não existe mais opt-out. Nesse mundo `setBackgroundColor` e
// `setOverlaysWebView` viraram no-op: o próprio plugin do Capacitor testa
// `shouldSetStatusBarColor()` e devolve false para API >= 36. Chamar essas duas
// funções aqui daria a falsa impressão de que a cor está sob controle do JS.
//
// Quem pinta a área da barra é o CSS da aplicação: o Capacitor injeta as
// variáveis de safe-area (o app já usa `viewport-fit=cover`) e as telas
// desenham navy ali — por exemplo o wrapper de TelaHome com
// `bg-[#12192a]` + `paddingTop: env(safe-area-inset-top)`.
//
// ⚠️ Esse repasse de insets exige **WebView 140+**. Abaixo disso o Capacitor
// empurra a WebView para baixo da barra e a faixa mostra o fundo da janela
// (branco). Foi o que apareceu no emulador, que vem com WebView 133 — em
// aparelho real de 2026 a WebView é bem mais nova. Se algum aparelho antigo
// mostrar a faixa branca, o `statusBarColor` do tema (styles.xml) cobre o caso.
//
// setStyle continua valendo em qualquer versão: usa WindowInsetsController,
// não a API de cor descontinuada.
const configurarBarraStatus = async () => {
  try {
    // Style.Dark = conteúdo CLARO. O header é navy #12192a com ícones brancos,
    // então relógio e bateria precisam ser brancos também.
    await StatusBar.setStyle({ style: Style.Dark });
  } catch {}
};

// ─── Splash nativa ───────────────────────────────────────────────────────────
// launchAutoHide está false no capacitor.config.json de propósito: se a splash
// sumisse sozinha por tempo, o usuário veria tela branca enquanto o React ainda
// monta. Some só quando a interface está de fato pronta.
export const esconderSplash = async () => {
  if (!ehNativo()) return;
  try {
    await SplashScreen.hide();
  } catch {}
};

// ─── Login com Google ────────────────────────────────────────────────────────
// O Google BLOQUEIA OAuth dentro de WebView embutida (erro 403
// disallowed_useragent). E politica dele, aplicada do lado de la desde 2021, e
// nao existe configuracao que desligue: um app dono da WebView poderia ler a
// senha digitada na tela do Google. Como "Entrar com Google" e o caminho que a
// maioria usa aqui, sem tratar isso o app fica inutilizavel na porta de entrada.
//
// A saida oficial e mandar o login pro NAVEGADOR DO SISTEMA (Chrome Custom Tab)
// e receber a resposta de volta por deep link. Bonus: como a Custom Tab
// compartilha a sessao do Chrome, quem ja esta logado no Google resolve num
// toque.
//
// ⚠️ Exige cadastrar "br.com.toalugando.app://auth" em Authentication →
//    URL Configuration → Redirect URLs no painel do Supabase. Sem isso o
//    Supabase recusa o redirect_to e o login para no meio.
export const ESQUEMA_APP = 'br.com.toalugando.app';
export const DESTINO_OAUTH_APP = `${ESQUEMA_APP}://auth`;

/**
 * Inicia o login com Google. Na web mantem o comportamento de sempre
 * (navega na propria aba); no app abre o navegador do sistema.
 */
export const iniciarLoginGoogle = async (supabaseUrl) => {
  const destino = ehNativo() ? DESTINO_OAUTH_APP : window.location.origin;
  const url =
    `${supabaseUrl}/auth/v1/authorize?provider=google` +
    `&redirect_to=${encodeURIComponent(destino)}&prompt=select_account`;

  if (!ehNativo()) {
    window.location.href = url;
    return;
  }
  try {
    await Browser.open({ url });
  } catch {
    // Ultimo recurso. Provavelmente cai no disallowed_useragent, mas e melhor
    // do que o botao nao fazer absolutamente nada.
    window.location.href = url;
  }
};

// Volta do navegador: br.com.toalugando.app://auth#access_token=...&refresh_token=...
//
// Em vez de duplicar a leitura dos tokens, reproduzimos a MESMA situacao da
// web: coloca o hash na URL e recarrega. Ai o utils/bootstrap.js le os tokens
// antes do React montar, exatamente como no site — nenhuma linha do App.js ou
// do AuthContext precisou mudar.
const tratarRetornoOAuth = ({ url } = {}) => {
  try {
    const bruta = String(url || '');
    const i = bruta.indexOf('#');
    if (i < 0) return;
    const hash = bruta.slice(i + 1);
    if (!new URLSearchParams(hash).get('access_token')) return;
    try { Browser.close(); } catch {}
    window.location.hash = hash;
    window.location.reload();
  } catch {}
};

// ─── Inicialização ───────────────────────────────────────────────────────────
let _iniciado = false;

/** Chamado uma vez no boot (src/index.js). Seguro chamar no navegador. */
export const iniciarNativo = () => {
  if (_iniciado || !ehNativo()) return;
  _iniciado = true;

  try {
    AppNativo.addListener('backButton', tratarVoltar);
  } catch {}

  try {
    AppNativo.addListener('appUrlOpen', tratarRetornoOAuth);
  } catch {}

  configurarBarraStatus();

  // Rede de segurança: se por qualquer motivo o React não chamar
  // esconderSplash(), a splash não pode ficar presa pra sempre.
  setTimeout(esconderSplash, 6000);
};
